import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import {
  BsArrowLeft,
  BsBoxSeam,
  BsPencilSquare,
  BsPlusLg,
  BsTools,
  BsUpcScan,
  BsWrench,
  BsChatLeftText,
  BsQrCodeScan,
  BsShieldCheck,
} from "react-icons/bs";

import Sidebar from "../../components/sidebar/Sidebar";

import SectionCard from "./SectionCard";
import JobSheetDrawerParts from "../../components/drawer/JobSheetDrawerParts";
import JobSheetDrawerFitting from "../../components/drawer/JobSheetDrawerFitting";
import JobSheetDrawerRepair from "../../components/drawer/JobSheetDrawerRepair";

import { serviceGetScaleJobDetails } from "../../api/serviceApi";
import { useUsers } from "../../hooks/useUsers";
import { useProducts } from "../../hooks/useProducts";

import { getImageUrl } from "../../utils/helper";

import "./jobsheetview.css";
import { isManager } from "../../auth/auth";

const fmtDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const getStatusClass = (status) => {
  if (!status) {
    return "unknown";
  }

  return String(status)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
};

const getId = (item) => item?.id || item?._id || "";

const getUserLabel = (user) => {
  if (user?.name) return user.name;
  if (user?.fullName) return user.fullName;
  if (user?.username) return user.username;
  if (user?.email) return user.email;

  return getId(user);
};

const FITTING_IMAGE_FIELDS = [
  {
    key: "beforePhotos",
    label: "Before Photos",
  },
  {
    key: "afterPhotos",
    label: "After Photos",
  },
  {
    key: "vinPlateSticker",
    label: "VIN Plate / Sticker",
  },
  {
    key: "scaleHeadUnit",
    label: "Scale Head Unit",
  },
  {
    key: "truckCleaned",
    label: "Truck Cleaned",
  },
  {
    key: "workAreaCleaned",
    label: "Work Area Cleaned",
  },
];

const getStoredImageValue = (image) => {
  if (typeof image === "string") {
    return image;
  }

  return image?.imageUrl || image?.url || "";
};

// components

const ViewJobSheet = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const barcodeRef = useRef(null);

  const { users } = useUsers(true);
  const { products } = useProducts(true);

  const [jobPartOpen, setJobPartOpen] = useState(false);
  const [jobFittingOpen, setJobFittingOpen] = useState(false);
  const [jobRepairOpen, setJobRepairOpen] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // lookup map
  const usersById = useMemo(() => {
    return new Map(users.map((user) => [getId(user), user]));
  }, [users]);

  const productsById = useMemo(() => {
    return new Map(products.map((product) => [getId(product), product]));
  }, [products]);

  // ==========================================================
  // LOAD JOB
  // ==========================================================

  const loadJob = useCallback(
    async (showLoading = true) => {
      try {
        if (showLoading) {
          setLoading(true);
        }

        const result = await serviceGetScaleJobDetails(jobId);

        setData(result);
      } catch (error) {
        console.error("Error loading scale job:", error);

        toast.error(error?.message || "Failed to load scale job.");

        if (showLoading) {
          setData(null);
        }
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [jobId],
  );

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  // ==========================================================
  // BARCODE
  // ==========================================================

  useEffect(() => {
    if (!data?.job?.barcode || !barcodeRef.current) {
      return;
    }

    // try {
    //   JsBarcode(barcodeRef.current, data.job.barcode, {
    //     format: "CODE128",
    //     lineColor: "#000000",
    //     width: 2,
    //     height: 52,
    //     displayValue: true,
    //     fontSize: 13,
    //     margin: 8,
    //   });
    // } catch (error) {
    //   console.error("Barcode render error:", error);
    // }

    const generateQRCode = async () => {
      try {
        await QRCode.toCanvas(barcodeRef.current, String(data.job.barcode), {
          width: 180,
          margin: 2,
          errorCorrectionLevel: "M",
          color: {
            dark: "#000000",
            light: "#ffffff",
          },
        });
      } catch (error) {
        console.error("QR code render error: ", error);
      }
    };

    generateQRCode();
  }, [data?.job?.barcode]);

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div className="job-view-page">
        <Sidebar />

        <main className="job-view-content">
          <div className="job-view-container">
            <div className="job-view-state">
              <div className="job-view-state-icon">
                <BsTools />
              </div>

              <strong>Loading scale job...</strong>

              <span>Please wait while the job details are loaded.</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ==========================================================
  // NOT FOUND
  // ==========================================================

  if (!data?.job) {
    return (
      <div className="job-view-page">
        <Sidebar />

        <main className="job-view-content">
          <div className="job-view-container">
            <div className="job-view-state">
              <div className="job-view-state-icon">
                <BsTools />
              </div>

              <strong>Scale job not found</strong>

              <span>
                The job may have been removed or is no longer available.
              </span>

              <button
                type="button"
                className="job-view-btn primary"
                onClick={() => navigate("/managejobs")}
              >
                <BsArrowLeft />
                Back to Jobs
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const { job, parts = [], fitting = null, repairs = [] } = data;

  const customer = job.customerDetails || {};
  const truck = job.truckDetails || {};
  const trailer = job.trailerDetails || {};
  const scale = job.scale || {};

  const statusClass = getStatusClass(job.status);

  // ==========================================================
  // SMS SEND job sheet
  // ==========================================================
  const handleSendSms = () => {
    const id = jobId;

    if (!id) {
      toast.error("Job ID is not available.");
      return;
    }

    const jobUrl = `https://tims.trucktronix.com.au/managejobs/view/${encodeURIComponent(id)}`;

    const message = `HaulWeigh Job Sheet:\n${jobUrl}`;

    /*
     * SMS URL format differs slightly between
     * iPhone and Android.
     */
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    const smsUrl = isIOS
      ? `sms:&body=${encodeURIComponent(message)}`
      : `sms:?body=${encodeURIComponent(message)}`;

    window.location.href = smsUrl;
  };

  return (
    <div className="job-view-page">
      <Sidebar />

      {/* ================================================
          DRAWERS
      ================================================= */}

      <JobSheetDrawerParts
        open={jobPartOpen}
        jobId={jobId}
        parts={parts}
        onClose={() => setJobPartOpen(false)}
        onSaved={() => loadJob(false)}
      />

      <JobSheetDrawerFitting
        open={jobFittingOpen}
        jobId={jobId}
        fitting={fitting}
        onClose={() => setJobFittingOpen(false)}
        onSaved={() => {
          setJobFittingOpen(false);
          loadJob(false);
        }}
      />

      <JobSheetDrawerRepair
        open={jobRepairOpen}
        jobId={jobId}
        onClose={() => setJobRepairOpen(false)}
        onSaved={() => {
          setJobRepairOpen(false);
          loadJob(false);
        }}
      />

      <main className="job-view-content">
        <div className="job-view-container">
          {/* ==============================================
              HEADER
          =============================================== */}

          <header className="job-view-header">
            <div className="job-view-heading">
              <button
                type="button"
                className="job-view-back"
                onClick={() => navigate("/managejobs")}
              >
                <BsArrowLeft />
                Back to Jobs
              </button>

              <div className="job-view-title-row">
                <h1>Scale Job</h1>

                <span className={`job-view-status status-${statusClass}`}>
                  {job.status || "Unknown"}
                </span>
              </div>

              <p>
                View customer, vehicle, scale, fitting and repair information.
              </p>
            </div>
          </header>

          {/* ==============================================
              MAIN CARD
          =============================================== */}

          <div className="job-view-card">
            {/* ============================================
                LEFT SIDEBAR
            ============================================= */}

            <aside className="job-view-sidebar">
              <div className="job-view-qr-header">
                <div className="job-view-barcode-icon">
                  <BsQrCodeScan />
                </div>

                <div className="job-view-sidebar-title">
                  <strong>HaulWeigh QR Code</strong>
                  <span>Unique scale job reference.</span>
                </div>
              </div>

              <div className="job-view-reference">
                <span>Product</span>
                <strong>{job.barcode || "—"}</strong>
              </div>

              {job.barcode && (
                <div className="job-view-barcode">
                  <canvas ref={barcodeRef} />
                </div>
              )}

              <div className="job-view-header-actions">
                <button
                  type="button"
                  className="job-view-btn ghost"
                  onClick={() => setJobPartOpen(true)}
                >
                  <BsPlusLg />
                  Add Part
                </button>

                <button
                  type="button"
                  className="job-view-btn ghost"
                  onClick={() => setJobFittingOpen(true)}
                >
                  <BsTools />

                  {fitting ? "Edit Fitting" : "Add Fitting"}
                </button>

                <button
                  type="button"
                  className="job-view-btn ghost"
                  onClick={() => setJobRepairOpen(true)}
                >
                  <BsWrench />
                  Add Repair
                </button>

                {/* <button
                  type="button"
                  className="job-view-btn primary"
                  onClick={() => setJobActivateOpen(true)}
                >
                  <BsShieldCheck />
                  Activate
                </button> */}
              </div>

              <div className="job-view-summary">
                <SummaryItem label="Status" value={job.status} />

                <SummaryItem label="Added" value={fmtDate(job.addedAt)} />

                <SummaryItem label="Updated" value={fmtDate(job.updatedAt)} />

                <SummaryItem
                  label="Added By"
                  value={
                    usersById.has(job.addedBy)
                      ? getUserLabel(usersById.get(job.addedBy))
                      : job.addedBy
                  }
                />
              </div>
            </aside>

            {/* ============================================
                DETAILS
            ============================================= */}

            <div className="job-view-details">
              {/* CUSTOMER */}

              <SectionCard
                title="Customer Details"
                description="Customer and company information for this job."
                defaultOpen
              >
                <div className="job-view-grid">
                  <Info label="Customer" value={customer.customerName} />

                  <Info label="Company" value={customer.companyName} />

                  <Info label="Address" value={customer.address} full />
                </div>
              </SectionCard>

              {/* SCALE */}

              <SectionCard
                title="Scale Details"
                description="Scale hardware and communication configuration."
                defaultOpen
              >
                <div className="job-view-grid">
                  <Info label="Scale Type" value={scale.scaleType} />

                  <Info label="Serial Number" value={scale.serialNumber} />

                  <Info
                    label="Server Serial Number"
                    value={scale.serverSerialNumber}
                  />

                  <Info label="ICCID" value={scale.iccid} />

                  <Info
                    label="Fleetmaster"
                    value={scale.fleetmaster ? "Yes" : "No"}
                  />
                </div>
              </SectionCard>

              {/* TRUCK */}

              <SectionCard
                title="Truck Details"
                description="Primary vehicle identification, configuration and axle sensors."
                defaultOpen
              >
                <div className="job-view-grid">
                  <Info label="Make" value={truck.make} />

                  <Info label="Year" value={truck.year} />

                  <Info label="Model" value={truck.model} />

                  <Info label="Body" value={truck.body} />

                  <Info label="Rego" value={truck.rego} />

                  <Info label="VIN" value={truck.vin} />

                  <Info label="Truck Config" value={truck.truckConfig} />

                  <Info
                    label="Steer Sensor Type"
                    value={truck.steerSensorType}
                  />

                  <Info
                    label="Drive Sensor Type"
                    value={truck.driveSensorType}
                  />
                </div>

                <Comments title="Truck Comments" values={truck.comments} />
              </SectionCard>

              {/* TRAILER */}

              <SectionCard
                title="Trailer Details"
                description="Trailer identification and axle sensor configuration."
                defaultOpen
              >
                <div className="job-view-grid">
                  <Info label="Make" value={trailer.make} />

                  <Info label="Year" value={trailer.year} />

                  <Info label="Model" value={trailer.model} />

                  <Info label="Body" value={trailer.body} />

                  <Info label="Rego" value={trailer.rego} />

                  <Info label="VIN" value={trailer.vin} />

                  <Info label="Trailer Config" value={trailer.trailerConfig} />

                  <Info
                    label="Trailer A Sensor Type"
                    value={trailer.trailerASensorType}
                  />

                  <Info
                    label="Trailer B Sensor Type"
                    value={trailer.trailerBSensorType}
                  />
                </div>

                <Comments title="Trailer Comments" values={trailer.comments} />
              </SectionCard>

              {/* PARTS */}

              <SectionCard
                title={`Parts Supplied (${parts.length})`}
                description="Inventory products supplied or allocated to this job."
                defaultOpen
                rightActions={
                  <button
                    type="button"
                    className="job-view-btn small ghost"
                    onClick={() => setJobPartOpen(true)}
                  >
                    <BsPlusLg />
                    Add Part
                  </button>
                }
              >
                {parts.length === 0 ? (
                  <EmptyMessage
                    icon={<BsBoxSeam />}
                    title="No parts added"
                    text="No inventory parts have been added to this job yet."
                  />
                ) : (
                  <div className="job-view-table-scroll">
                    <table className="job-view-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Qty</th>
                          <th>Status</th>
                          <th>Comment</th>
                        </tr>
                      </thead>

                      <tbody>
                        {parts.map((part) => (
                          <tr key={part.id || part._id}>
                            <td>
                              <PartDisplay part={part} />
                            </td>

                            <td>
                              <span className="job-view-qty">
                                {part.quantity ?? 0}
                              </span>
                            </td>

                            <td>{part.action || "—"}</td>

                            <td>{part.comment || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>

              {/* FITTING */}

              <SectionCard
                title="Fitting"
                description="Installation date, fitters and fitting notes."
                defaultOpen
                rightActions={
                  <button
                    type="button"
                    className="job-view-btn small ghost"
                    onClick={() => setJobFittingOpen(true)}
                  >
                    <BsTools />

                    {fitting ? "Edit Fitting" : "Add Fitting"}
                  </button>
                }
              >
                {!fitting ? (
                  <EmptyMessage
                    icon={<BsTools />}
                    title="No fitting recorded"
                    text="Fitting information has not been added to this job."
                  />
                ) : (
                  <>
                    <div className="job-view-grid">
                      <Info
                        label="Fitting Date"
                        value={fmtDate(fitting.fittingDate)}
                      />

                      <div className="job-view-info">
                        <span className="job-view-label">Fitters</span>

                        <UserList
                          ids={fitting.fitterIds}
                          usersById={usersById}
                        />
                      </div>
                    </div>

                    <FittingImages images={fitting.fittingImages} />

                    <Comments
                      title="Fitting Comments"
                      values={fitting.comments}
                    />
                  </>
                )}
              </SectionCard>

              {/* REPAIRS */}

              <SectionCard
                title={`Repairs (${repairs.length})`}
                description="Repair history, repairers, images and replacement parts."
                defaultOpen
                rightActions={
                  <button
                    type="button"
                    className="job-view-btn small primary"
                    onClick={() => setJobRepairOpen(true)}
                  >
                    <BsWrench />
                    Add Repair
                  </button>
                }
              >
                {repairs.length === 0 ? (
                  <EmptyMessage
                    icon={<BsWrench />}
                    title="No repairs recorded"
                    text="No repair work has been recorded for this job."
                  />
                ) : (
                  <div className="job-view-repair-list">
                    {repairs.map((repair, index) => (
                      <RepairCard
                        key={repair.id || repair._id || index}
                        repair={repair}
                        index={index}
                        usersById={usersById}
                        productsById={productsById}
                      />
                    ))}
                  </div>
                )}
              </SectionCard>

              {/* BOTTOM ACTIONS */}

              <div className="job-view-bottom-actions">
                <button
                  type="button"
                  className="job-view-btn ghost"
                  onClick={() => navigate("/managejobs")}
                >
                  <BsArrowLeft />
                  Back
                </button>

                {isManager() && (
                  <>
                    <button
                      type="button"
                      className="job-view-btn ghost"
                      onClick={() => navigate(`/managejobs/edit/${jobId}`)}
                    >
                      <BsPencilSquare />
                      Edit Job
                    </button>

                    <button
                      type="button"
                      className="job-view-btn primary"
                      onClick={handleSendSms}
                    >
                      <BsChatLeftText />
                      Send SMS
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// ============================================================
// INFO
// ============================================================

const Info = ({ label, value, full = false }) => {
  const displayValue =
    value === null || value === undefined || value === "" ? "—" : String(value);

  return (
    <div className={`job-view-info ${full ? "job-view-info-full" : ""}`}>
      <span className="job-view-label">{label}</span>

      <span className="job-view-value">{displayValue}</span>
    </div>
  );
};

// ============================================================
// SUMMARY
// ============================================================

const SummaryItem = ({ label, value }) => (
  <div className="job-view-summary-item">
    <span>{label}</span>

    <strong>
      {value === null || value === undefined || value === ""
        ? "—"
        : String(value)}
    </strong>
  </div>
);

// ============================================================
// COMMENTS
// ============================================================

const Comments = ({ title, values }) => {
  if (!Array.isArray(values) || values.length === 0) {
    return null;
  }

  const comments = values
    .map((comment) => {
      if (typeof comment === "string") {
        return comment;
      }

      return comment?.text || "";
    })
    .filter(Boolean);

  if (comments.length === 0) {
    return null;
  }

  return (
    <div className="job-view-comments">
      <strong>{title}</strong>

      <ul>
        {comments.map((comment, index) => (
          <li key={index}>{comment}</li>
        ))}
      </ul>
    </div>
  );
};

// ============================================================
// EMPTY
// ============================================================

const EmptyMessage = ({ icon, title, text }) => (
  <div className="job-view-empty">
    <div className="job-view-empty-icon">{icon}</div>

    <strong>{title}</strong>

    <span>{text}</span>
  </div>
);

// ============================================================
// PART DISPLAY
// ============================================================

const PartDisplay = ({ part, productsById }) => {
  const product = productsById?.get(part.productId);

  const name = part.name || product?.name || "Unknown product";

  const mpn = part.mpn || product?.mpn || "";

  const storedImage = part.imageUrl || product?.imageUrl || "";

  const imageUrl = getImageUrl(storedImage);

  return (
    <div className="job-view-part-product">
      <div className="job-view-part-image-wrap">
        {imageUrl ? (
          <img className="job-view-part-image" src={imageUrl} alt={name} />
        ) : (
          <BsBoxSeam />
        )}
      </div>

      <div className="job-view-part-details">
        <strong>{name}</strong>

        {mpn ? (
          <small>MPN: {mpn}</small>
        ) : part.productId ? (
          <small>{part.productId}</small>
        ) : null}
      </div>
    </div>
  );
};

// ============================================================
// REPAIR
// ============================================================

const RepairCard = ({ repair, index, usersById, productsById }) => {
  const parts = repair.parts || [];

  return (
    <article className="job-view-repair-card">
      <div className="job-view-repair-header">
        <div>
          <span>Repair {index + 1}</span>

          <strong>{fmtDate(repair.repairDate)}</strong>
        </div>

        <div className="job-view-repair-users">
          <UserList ids={repair.repairerIds} usersById={usersById} />
        </div>
      </div>

      <Comments title="Comments" values={repair.comments} />

      {repair.repairImages?.length > 0 && (
        <div className="job-view-repair-images">
          {repair.repairImages.map((image, imageIndex) => {
            const imageUrl = getImageUrl(image.imageUrl);

            return (
              <a
                className="job-view-repair-image"
                key={imageIndex}
                href={imageUrl}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src={imageUrl}
                  alt={`Repair ${index + 1} image ${imageIndex + 1}`}
                />

                {image.comment && <span>{image.comment}</span>}
              </a>
            );
          })}
        </div>
      )}

      <div className="job-view-repair-parts">
        <div className="job-view-repair-parts-header">
          <strong>Repair Parts ({parts.length})</strong>
        </div>

        {parts.length === 0 ? (
          <div className="job-view-repair-empty">No repair parts.</div>
        ) : (
          <div className="job-view-table-scroll">
            <table className="job-view-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Comment</th>
                </tr>
              </thead>

              <tbody>
                {parts.map((part) => (
                  <tr key={part.id || part._id}>
                    <td>
                      <PartDisplay part={part} productsById={productsById} />
                    </td>

                    <td>
                      <span className="job-view-qty">{part.quantity ?? 0}</span>
                    </td>

                    <td>{part.comment || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </article>
  );
};

const FittingImages = ({ images }) => {
  if (!images) {
    return null;
  }

  const groups = FITTING_IMAGE_FIELDS.map((field) => ({
    ...field,

    images: Array.isArray(images[field.key]) ? images[field.key] : [],
  })).filter((group) => group.images.length > 0);

  if (groups.length === 0) {
    return null;
  }

  return (
    <div className="job-view-fitting-images">
      <strong className="job-view-subheading">Fitting Photos</strong>

      <div className="job-view-fitting-image-groups">
        {groups.map((group) => (
          <div key={group.key} className="job-view-fitting-image-group">
            <div className="job-view-fitting-image-header">
              <span>{group.label}</span>

              <small>
                {group.images.length} photo
                {group.images.length === 1 ? "" : "s"}
              </small>
            </div>

            <div className="job-view-fitting-image-grid">
              {group.images.map((image, index) => {
                const storedValue = getStoredImageValue(image);

                const imageUrl = getImageUrl(storedValue);

                if (!imageUrl) {
                  return null;
                }

                return (
                  <a
                    key={`${storedValue}-${index}`}
                    className="job-view-fitting-image"
                    href={imageUrl}
                    target="_blank"
                    rel="noreferrer"
                    title={`Open ${group.label} photo ${index + 1}`}
                  >
                    <img src={imageUrl} alt={`${group.label} ${index + 1}`} />

                    <span>{index + 1}</span>
                  </a>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const UserList = ({ ids, usersById }) => {
  if (!Array.isArray(ids) || ids.length === 0) {
    return <span className="job-view-value">—</span>;
  }

  return (
    <div className="job-view-user-list">
      {ids.map((id) => {
        const user = usersById.get(id);

        return (
          <span key={id} className="job-view-user">
            {user ? getUserLabel(user) : id}
          </span>
        );
      })}
    </div>
  );
};

export default ViewJobSheet;
